import {
  Cell,
  CellMetadata,
  CellMode,
  CellValue,
} from '@/shared/ui/data/EditableGrid/types/cell.types';
import { calculateDiff } from '@/shared/ui/data/EditableGrid/lib/diff';
import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';
import { Column } from '@/shared/ui/data/EditableGrid/types/column.types';

export function createCell<T extends CellValue>(
  position: Position,
  value: T,
  initial: T,
  editable: boolean = true
): Cell<T> {
  const diff = calculateDiff(value, initial);
  const mode = editable ? 'idle' : 'disabled';

  return {
    position,
    value,
    initial,
    diff,
    mode,
  };
}

export function buildMetadata(col: Column): CellMetadata {
  return col;
}

export function updateCellValue<T extends CellValue>(cell: Cell<T>, newValue: T): Cell<T> {
  const initial = cell.initial;
  const diff = calculateDiff(newValue, initial);
  return {
    ...cell,
    value: newValue,
    initial: initial,
    diff: diff,
  };
}

export function setCellMode<T extends CellValue>(cell: Cell<T>, mode: CellMode): Cell<T> {
  return { ...cell, mode };
}

export function focusCell<T extends CellValue>(cell: Cell<T>): Cell<T> {
  return setCellMode(cell, 'focused');
}

export function blurCell<T extends CellValue>(cell: Cell<T>): Cell<T> {
  return setCellMode(cell, 'idle');
}

export function selectCell<T extends CellValue>(cell: Cell<T>): Cell<T> {
  return setCellMode(cell, 'selected');
}

export function startEditingCell<T extends CellValue>(cell: Cell<T>): Cell<T> {
  if (cell.mode === 'disabled') throw new Error('Cannot edit disabled cell');
  return setCellMode(cell, 'editing');
}

export function stopEditingCell<T extends CellValue>(cell: Cell<T>): Cell<T> {
  return setCellMode(cell, 'focused');
}
